import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lock, MessageSquare, Trash2, RefreshCw, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'uandfashion_admin_key';

const AdminContactMessagesPage = () => {
  const [keyInput, setKeyInput] = useState('');
  const [authKey, setAuthKey] = useState(() => sessionStorage.getItem(STORAGE_KEY) || '');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const fetchList = useCallback(async (key) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/contact-messages/list`, { params: { key } });
      setMessages(res.data.messages || []);
      sessionStorage.setItem(STORAGE_KEY, key);
      setAuthKey(key);
    } catch (err) {
      setError(
        err?.response?.status === 403
          ? 'Onjuist wachtwoord — probeer opnieuw.'
          : 'Kan de lijst niet ophalen. Controleer je verbinding.'
      );
      sessionStorage.removeItem(STORAGE_KEY);
      setAuthKey('');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authKey) fetchList(authKey);
  }, [authKey, fetchList]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (keyInput.trim()) fetchList(keyInput.trim());
  };

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAuthKey('');
    setKeyInput('');
    setMessages([]);
  };

  const handleDelete = async (msg) => {
    const ok = window.confirm(
      `Weet je zeker dat je het bericht van ${msg.name} (${msg.email}) wil verwijderen?`
    );
    if (!ok) return;
    setDeleting(msg.id);
    try {
      await axios.delete(`${API}/contact-messages/${msg.id}`, { params: { key: authKey } });
      setMessages((prev) => prev.filter((m) => m.id !== msg.id));
    } catch (err) {
      alert('Verwijderen mislukt. Probeer opnieuw.');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  if (!authKey) {
    return (
      <div
        className="min-h-screen bg-gray-50 flex items-center justify-center px-4"
        data-testid="contact-admin-login"
      >
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <div className="flex flex-col items-center mb-6">
            <div className="bg-gradient-to-br from-pink-500 via-purple-500 to-indigo-500 p-3 rounded-full mb-4">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Beheer — Contactberichten</h1>
            <p className="text-sm text-gray-500 text-center">
              Voer je beheerderswachtwoord in om contactberichten te bekijken.
            </p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="Wachtwoord"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="contact-admin-key-input"
              autoFocus
            />
            <button
              type="submit"
              disabled={loading || !keyInput.trim()}
              className="w-full bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-60"
              data-testid="contact-admin-login-button"
            >
              {loading ? 'Bezig...' : 'Inloggen'}
            </button>
            {error && (
              <p className="text-sm text-red-600 text-center" data-testid="contact-admin-error">
                {error}
              </p>
            )}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="contact-admin-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Contactberichten</h1>
            <p className="text-sm text-gray-500 mt-1">
              Berichten die bezoekers achterlaten via de "Contact"-pagina.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => fetchList(authKey)}
              className="inline-flex items-center gap-2 bg-white border border-gray-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50"
              data-testid="contact-admin-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Vernieuwen
            </button>
            <button
              onClick={handleLogout}
              className="text-sm text-gray-500 hover:text-gray-700 px-3"
              data-testid="contact-admin-logout"
            >
              Uitloggen
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex items-center gap-3 text-gray-500 text-sm mb-1">
            <MessageSquare className="w-4 h-4" /> Totaal berichten
          </div>
          <p className="text-3xl font-bold text-gray-900" data-testid="contact-admin-stat-total">
            {messages.length}
          </p>
        </div>

        <div className="space-y-3">
          {loading && messages.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center text-gray-500">
              Lijst aan het laden…
            </div>
          ) : messages.length === 0 ? (
            <div
              className="bg-white rounded-xl p-12 text-center text-gray-500"
              data-testid="contact-admin-empty"
            >
              Nog geen berichten. Zodra iemand het formulier op /contact invult, verschijnt het hier.
            </div>
          ) : (
            messages.map((msg) => {
              const isOpen = expanded === msg.id;
              return (
                <div
                  key={msg.id}
                  className="bg-white rounded-xl shadow-sm overflow-hidden"
                  data-testid={`contact-admin-row-${msg.id}`}
                >
                  <button
                    onClick={() => setExpanded(isOpen ? null : msg.id)}
                    className="w-full px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-left hover:bg-gray-50"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">
                        {msg.subject || '(geen onderwerp)'}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {msg.name} · {msg.email}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span>{formatDate(msg.submitted_at)}</span>
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-purple-50 text-purple-700 font-medium">
                        {isOpen ? 'Sluiten' : 'Details'}
                      </span>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t border-gray-100 px-6 py-5 bg-gray-50/50 space-y-4">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <a
                          href={`mailto:${msg.email}`}
                          className="text-purple-600 hover:text-purple-800 underline break-all"
                        >
                          {msg.email}
                        </a>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Bericht</p>
                        <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">{msg.message}</p>
                      </div>
                      <div className="flex justify-end pt-2">
                        <button
                          onClick={() => handleDelete(msg)}
                          disabled={deleting === msg.id}
                          className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50"
                          data-testid={`contact-admin-delete-${msg.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                          {deleting === msg.id ? 'Bezig…' : 'Verwijderen'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <p className="text-xs text-gray-400 mt-8 text-center">
          Je sessie wordt actief gehouden in deze browser. Klik op "Uitloggen" om hem te beëindigen.
        </p>
      </div>
    </div>
  );
};

export default AdminContactMessagesPage;
